import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, UserRole } from '../lib/supabase';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscriptionLoading: boolean;
  userRole: 'owner' | 'agency_admin' | 'company_admin' | null;
  userRoles: UserRole[];
  viewingCompanyId: string | null;
  viewingAgencyId: string | null;
  subscriptionStatus: 'pending' | 'active' | 'past_due' | 'cancelled' | null;
  hasActiveSubscription: boolean;
  setViewingCompanyId: (companyId: string | null) => void;
  setViewingAgencyId: (agencyId: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'owner' | 'agency_admin' | 'company_admin' | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [viewingCompanyId, setViewingCompanyId] = useState<string | null>(null);
  const [viewingAgencyId, setViewingAgencyId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'pending' | 'active' | 'past_due' | 'cancelled' | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const checkUserRoles = async (userId: string) => {
    setSubscriptionLoading(true);
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      setSubscriptionLoading(false);
      return;
    }

    if (data && data.length > 0) {
      setUserRoles(data);
      if (data.some(r => r.role === 'owner')) {
        setUserRole('owner');
        setSubscriptionLoading(false);
      } else if (data.some(r => r.role === 'agency_admin')) {
        setUserRole('agency_admin');
        setSubscriptionLoading(false);
      } else if (data.some(r => r.role === 'company_admin')) {
        setUserRole('company_admin');
      }

      const companyRole = data.find(r => r.company_id);
      if (companyRole?.company_id) {
        await checkSubscriptionStatus(companyRole.company_id);
      } else {
        setSubscriptionLoading(false);
      }
    } else {
      setUserRoles([]);
      setUserRole(null);
      setSubscriptionStatus(null);
      setHasActiveSubscription(false);
      setSubscriptionLoading(false);
    }
  };

  const checkSubscriptionStatus = async (companyId: string) => {
    const { data, error } = await supabase
      .from('companies')
      .select('subscription_status')
      .eq('id', companyId)
      .maybeSingle();

    if (error) {
      setSubscriptionLoading(false);
      return;
    }

    if (data) {
      setSubscriptionStatus(data.subscription_status);
      setHasActiveSubscription(data.subscription_status === 'active');
    } else {
      setSubscriptionStatus(null);
      setHasActiveSubscription(false);
    }
    setSubscriptionLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkUserRoles(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await checkUserRoles(session.user.id);
        } else {
          setUserRole(null);
          setUserRoles([]);
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
    }

    setUser(null);
    setSession(null);
    setUserRole(null);
    setUserRoles([]);
    setSubscriptionStatus(null);
    setHasActiveSubscription(false);
    setSubscriptionLoading(true);
    setViewingCompanyId(null);
    setViewingAgencyId(null);

    try {
      localStorage.removeItem('supabase.auth.token');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
    } catch {
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscriptionLoading,
        userRole,
        userRoles,
        viewingCompanyId,
        viewingAgencyId,
        subscriptionStatus,
        hasActiveSubscription,
        setViewingCompanyId,
        setViewingAgencyId,
        signIn,
        signUp,
        signOut,
        resetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
